import { AvcorePublishStreamApiCredentials, StreamId } from "@common/domain";
import debug from "debug";
import { RtpCapabilities } from "mediasoup-client/lib/RtpParameters";
import {
  EMPTY,
  firstValueFrom,
  lastValueFrom,
  merge,
  Observable,
  ReplaySubject,
  Subject
} from "rxjs";
import { concatMap, finalize, map, skip, takeUntil } from "rxjs/operators";
import { StreamData } from "../../actions";
import { ExtendableError } from "../../common";
import { ExtendedMediaStream } from "../../extended-media-stream";
import {
  ConferenceApiScore,
  ConferenceApiStats,
  PublishConferenceApi
} from "../conferenceApi";
import { BaseConferenceStream } from "./BaseConferenceStream";

export abstract class LocalConferenceStream extends BaseConferenceStream {
  private publishConferenceApi: null | PublishConferenceApi = null;
  private readonly debug: debug.Debugger;

  private readonly _lastScore$ = new Subject<ConferenceApiScore>();
  readonly lastScore$ = this._lastScore$.asObservable();

  private readonly _stats$ = new ReplaySubject<ConferenceApiStats[]>(1);
  readonly stats$ = this._stats$.asObservable();

  private readonly _recvRtpCapabilities$ = new ReplaySubject<RtpCapabilities>(
    1
  );
  readonly recvRtpCapabilities$ = this._recvRtpCapabilities$.asObservable();

  private readonly _sendRtpCapabilities$ = new ReplaySubject<RtpCapabilities>(
    1
  );
  readonly sendRtpCapabilities$ = this._sendRtpCapabilities$.asObservable();

  private readonly _streamDone$ = new ReplaySubject<void>(1);
  private readonly _destroy$ = new Subject<void>();

  constructor(
    streamId: StreamId,
    private readonly logger: (...args: unknown[]) => void
  ) {
    super(streamId);

    this._stream$
      .pipe(
        // stop listen new streams right away
        takeUntil(this._destroy$),
        concatMap((_) =>
          _
            ? this.listenTracks(
                _,
                merge(this._stream$.pipe(skip(1)), this._destroy$)
              )
            : EMPTY
        ),
        finalize(() => {
          this._streamDone$.next();
          this._streamDone$.complete();
        })
      )
      .subscribe();

    this.debug = debug("youm-lib:LocalConferenceStream");
  }

  get streamApiCredentials() {
    return this.publishConferenceApi?.streamApiCredentials || null;
  }

  get streamData() {
    return this.publishConferenceApi?.streamData || null;
  }

  override async destroy() {
    const streamDone = lastValueFrom(this._streamDone$);

    this._destroy$.next();
    this._destroy$.complete();

    // Make sure all actions on a conference api is done before destroy
    await streamDone;
    await this.publishConferenceApi?.destroy();

    this._lastScore$.complete();
    this._stats$.complete();

    super.destroy();
  }

  // LocalConferenceStream instance is supposed to work with a media in a sync context before the publishing.
  // The publish process can be done in an async way.
  async publish(
    streamData: StreamData,
    streamApiCredentials: AvcorePublishStreamApiCredentials
  ) {
    if (this.publishConferenceApi) {
      throw new Error("LocalConferenceStream already published");
    }

    const publishConferenceApi = new PublishConferenceApi(
      streamData,
      streamApiCredentials,
      this.logger
    );

    publishConferenceApi.lastScore$.subscribe(this._lastScore$);
    publishConferenceApi.stats$.subscribe(this._stats$);
    publishConferenceApi.recvRtpCapabilities$.subscribe(
      this._recvRtpCapabilities$
    );
    publishConferenceApi.sendRtpCapabilities$.subscribe(
      this._sendRtpCapabilities$
    );

    try {
      const stream = await firstValueFrom(this._stream$);
      if (!stream) {
        return;
      }

      await publishConferenceApi.publish(stream);
      this.publishConferenceApi = publishConferenceApi;
    } catch (error) {
      await publishConferenceApi.destroy().catch((error2) => {
        throw new ExtendableError(error.message, error, error2);
      });

      throw error;
    }

    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private listenTracks(stream: ExtendedMediaStream, stop$: Observable<any>) {
    return merge(
      stream.addTrack$.pipe(map((_) => ({ addtrack: _ }))),
      stream.removeTrack$.pipe(map((_) => ({ removetrack: _ })))
    ).pipe(
      // completes the source
      // wait concatMap is done, should complete naturally to avoid interruption and RC
      takeUntil(stop$),
      // Update stream's tracks sequentially
      // in order to avoid RC between the same kinds of streams
      concatMap((_) =>
        // A rejection here would end the pipeline and ignore every later
        // device change for this stream, so log it and keep listening.
        ("addtrack" in _
          ? this.addTrack(_.addtrack)
          : this.removeTrack(_.removetrack)
        ).catch((error) =>
          this.logger(
            `LocalConferenceStream ${
              "addtrack" in _ ? "addTrack" : "removeTrack"
            } failed`,
            error
          )
        )
      )
    );
  }

  private async addTrack(track: MediaStreamTrack) {
    return this.publishConferenceApi?.addTrack(track);
  }

  private async removeTrack(track: MediaStreamTrack) {
    return this.publishConferenceApi?.removeTrack(track);
  }
}
